import React, { useCallback, useState } from "react";

/** The keyframe the kit's entrances play (styles/base/motion.scss). */
const ENTRANCE_KEYFRAME: string = "kuika-row-in";

/** Marks the element whose animation closes an entrance sequence. */
const ENTRANCE_LAST_ATTRIBUTE: string = "data-entrance-last";

export interface IMountEntrance {
  /** True from the first render until the entrance has played; put the entering class on it. */
  isEntering: boolean;
  /** Attach to the surface's root while `isEntering`; animationend bubbles up to it. */
  onAnimationEnd: (event: React.AnimationEvent<HTMLElement>) => void;
}

/**
 * An entrance that plays once, when the surface mounts (KuikaEmptyState, KuikaCreateHero).
 *
 * The surface renders its entering class while `isEntering` and marks the element whose animation
 * ends the sequence with `data-entrance-last` (the last staggered child, or the one animated
 * element). When that animation ends the class comes off for good: a re-render keeps the class
 * string stable while it plays and never adds it back, so the entrance cannot replay. Reduced
 * motion is the keyframe's own business — its travel is multiplied by --motion-travel.
 */
const useMountEntrance = (): IMountEntrance => {
  const [isEntering, setIsEntering] = useState<boolean>(true);

  const onAnimationEnd = useCallback((event: React.AnimationEvent<HTMLElement>): void => {
    const target: EventTarget = event.target;
    if (event.animationName !== ENTRANCE_KEYFRAME || !(target instanceof Element)) {
      return;
    }
    if (target.hasAttribute(ENTRANCE_LAST_ATTRIBUTE)) {
      setIsEntering(false);
    }
  }, []);

  return { isEntering, onAnimationEnd };
};

export default useMountEntrance;
